import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ReactFlow, { Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import { palette } from '../../utils/designTokens';
import GraphNode from '../../components/GraphNode';
import { EMPTY_DATA_MESSAGE, GRAPH_MINIMAP_SHOW, GRAPH_MINIMAP_HIDE, GRAPH_MINIMAP_LABEL } from '../../utils/strings';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { fetchSessionEdges } from '../../services/traceDetailApi';
import { buildAgentGraph, EDGE_KIND_LABELS, EDGE_KIND_STYLES } from '../../utils/agentGraphBuilder';

const EdgeDetail = ({ item, onClose }) => {
  const { edges: edgeList, kind, sourceLabel, targetLabel } = item.data;
  const multiTrace = new Set((edgeList || []).map(e => e.trace_id).filter(Boolean)).size > 1;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle shrink-0">
        <div className="flex items-center gap-2 text-xs min-w-0">
          <span className="font-medium text-text-primary truncate">{sourceLabel}</span>
          <span className="text-text-muted shrink-0">→</span>
          <span className="font-medium text-text-primary truncate">{targetLabel}</span>
          <span className="shrink-0 text-text-muted bg-background-tertiary px-1.5 py-0.5 rounded">
            {EDGE_KIND_LABELS[kind] || kind}
          </span>
          {edgeList && edgeList.length > 1 && (
            <span className="shrink-0 text-text-muted">{edgeList.length} calls</span>
          )}
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary text-xs px-1 shrink-0 ml-1">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-border-subtle">
        {(edgeList || []).map((edge, i) => (
          <div key={i} className="px-3 py-2">
            <div className="flex items-center gap-3 mb-1 text-xs text-text-muted flex-wrap">
              {edgeList.length > 1 && <span>#{i + 1}</span>}
              {multiTrace && edge.trace_id && (
                <span className="font-mono">{edge.trace_id.slice(0, 8)}</span>
              )}
              {edge.duration_ms != null && <span>{edge.duration_ms}ms</span>}
              {(edge.input_tokens || edge.output_tokens) && (
                <span>{edge.input_tokens ?? 0} in / {edge.output_tokens ?? 0} out tokens</span>
              )}
            </div>
            {edge.input_summary && (
              <div className="text-xs mb-0.5">
                <span className="text-text-muted">In: </span>
                <span className="text-text-primary">{edge.input_summary}</span>
              </div>
            )}
            {edge.output_summary && (
              <div className="text-xs">
                <span className="text-text-muted">Out: </span>
                <span className="text-text-primary">{edge.output_summary}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const NodeDetail = ({ item, onClose }) => {
  const { label, type, callers, callees, totalCalls, totalTokens } = item.data;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle shrink-0">
        <div className="flex items-center gap-2 text-xs min-w-0">
          <span className="font-medium text-text-primary truncate">{label}</span>
          <span className="text-text-muted capitalize shrink-0">{type}</span>
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary text-xs px-1 shrink-0 ml-1">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        <div className="text-xs">
          <span className="text-text-muted">Callers: </span>
          {callers && callers.length > 0
            ? <span className="text-text-primary">{callers.join(', ')}</span>
            : <span className="text-text-muted italic">Root node</span>
          }
        </div>
        {callees && callees.length > 0 && (
          <div className="text-xs">
            <span className="text-text-muted">Calls: </span>
            <span className="text-text-primary">{callees.join(', ')}</span>
          </div>
        )}
        <div className="text-xs">
          <span className="text-text-muted">Total calls: </span>
          <span className="text-text-primary">{totalCalls ?? 0}</span>
        </div>
        {totalTokens != null && totalTokens > 0 && (
          <div className="text-xs">
            <span className="text-text-muted">Total tokens (as source): </span>
            <span className="text-text-primary">{totalTokens}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailPanelContent = ({ selectedItem, onClose }) => {
  if (selectedItem?.type === 'edge') return <EdgeDetail item={selectedItem} onClose={onClose} />;
  if (selectedItem?.type === 'node') return <NodeDetail item={selectedItem} onClose={onClose} />;
  return (
    <div className="flex h-full items-center justify-center p-4">
      <span className="text-sm text-text-muted text-center">Click a node or edge to see details</span>
    </div>
  );
};


const SessionAgentGraph = ({ sessionId, appId, env, sessionJwt }) => {
  const [apiEdges, setApiEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [isInteractive, setIsInteractive] = useState(true);
  const [panelWidth, setPanelWidth] = useState(320);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!sessionId || !sessionJwt) return;
    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setSelectedItem(null);
    fetchSessionEdges(sessionId, appId, env, sessionJwt)
      .then(data => { if (!cancelled) { setApiEdges(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setFetchError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [sessionId, appId, env, sessionJwt]);

  const nodeTypes = useMemo(() => ({ graphNode: GraphNode }), []);

  const { layoutedNodes, baseEdges } = useMemo(() => {
    if (!apiEdges.length) return { layoutedNodes: [], baseEdges: [] };
    return buildAgentGraph(apiEdges);
  }, [apiEdges]);

  const { rfNodes, rfEdges } = useMemo(() => {
    const selEdgeId = selectedItem?.type === 'edge' ? selectedItem.id : null;
    const selNodeId = selectedItem?.type === 'node' ? selectedItem.id : null;
    return {
      rfNodes: layoutedNodes.map(n => ({ ...n, selected: n.id === selNodeId })),
      rfEdges: baseEdges.map(e => {
        if (e.id !== selEdgeId) return e;
        return { ...e, selected: true, style: { ...e.style, strokeWidth: e.style.strokeWidth + 1 } };
      }),
    };
  }, [layoutedNodes, baseEdges, selectedItem]);

  const onNodeClick = useCallback((event, node) => {
    event.stopPropagation();
    setSelectedItem({ type: 'node', id: node.id, data: node.data });
  }, []);

  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setSelectedItem({ type: 'edge', id: edge.id, data: edge.data });
  }, []);

  // Panel stays open on canvas click — use ✕ to close
  const onPaneClick = useCallback(() => {}, []);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = Math.min(520, Math.max(240, containerRect.right - e.clientX));
    setPanelWidth(newWidth);
  }, []);

  const handleResizeUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeUp);
      };
    }
  }, [isDragging, handleResizeMove, handleResizeUp]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-text-muted text-sm">Loading agent flow...</div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-status-danger text-sm">Failed to load edges: {fetchError}</div>
      </div>
    );
  }

  if (!rfNodes.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-text-muted">{EMPTY_DATA_MESSAGE}</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div ref={containerRef} className="flex h-full flex-row">
        <div className="flex-1 min-w-0">
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={nodeTypes}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            fitView
            proOptions={{ hideAttribution: true }}
            panOnDrag={isInteractive}
            zoomOnScroll={isInteractive}
            zoomOnPinch={isInteractive}
            zoomOnDoubleClick={isInteractive}
            style={{ background: palette.neutrals.canvas }}
            onlyRenderVisibleElements={true}
            elevateNodesOnSelect={false}
            nodeOrigin={[0.5, 0.5]}
          >
            <Controls
              showInteractive={true}
              onInteractiveChange={(interactive) => setIsInteractive(interactive)}
            />
            {showMiniMap && (
              <MiniMap
                nodeColor={palette.text.primary}
                nodeStrokeColor={palette.text.primary}
                maskColor="rgba(0, 0, 0, 0.7)"
                style={{
                  background: palette.neutrals.canvas,
                  border: `2px solid ${palette.neutrals.borderStrong}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                }}
                position="bottom-right"
                nodeBorderRadius={4}
                nodeStrokeWidth={10}
                zoomable
                pannable
              />
            )}
            <div className="absolute z-10" style={{ bottom: '20px', right: '20px' }}>
              <button
                onClick={() => setShowMiniMap(!showMiniMap)}
                className="px-3 py-1.5 text-xs bg-background-secondary border border-border-subtle rounded hover:bg-surface-hover text-text-secondary hover:text-text-primary transition-colors shadow-md"
                title={showMiniMap ? 'Hide navigation minimap' : 'Show navigation minimap'}
              >
                {showMiniMap ? GRAPH_MINIMAP_HIDE : GRAPH_MINIMAP_SHOW} {GRAPH_MINIMAP_LABEL}
              </button>
            </div>
          </ReactFlow>
        </div>

        <div
          className="w-1 shrink-0 cursor-col-resize hover:w-1.5 transition-all relative"
          style={{ backgroundColor: isDragging ? palette.accents.primary.base : palette.borders.subtle }}
          onMouseDown={handleResizeStart}
        >
          <div
            className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 rounded-full opacity-0 hover:opacity-100 transition-opacity"
            style={{ backgroundColor: palette.accents.primary.base }}
          />
        </div>

        <div
          className="shrink-0 flex flex-col border-l border-border-subtle bg-background-secondary overflow-hidden relative"
          style={{ width: `${panelWidth}px`, minWidth: '240px', maxWidth: '520px' }}
        >
          <DetailPanelContent selectedItem={selectedItem} onClose={() => setSelectedItem(null)} />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SessionAgentGraph;
